// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import {
  DEFAULT_PAGE,
  PAGE_SIZE,
  DATE_FORMAT,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';
import { GetEmissionParams, Site, EmissionStatus } from 'util/Types';
import {
  WorkHistoriesEmissionsData,
  WorkHistoriesParams,
} from 'types/workHistoriesTypes';
import styles from './HistoryEmissions.module.scss';
import { RoleType, PrintLabel } from 'util/Enums';
import { Column } from 'react-table';
import {
  getFormatDateWithCurrentTimezone,
  getUtcFormatDateTime,
} from 'ts/formatDate';
import {
  getBrightnessFromColor,
  mergeClasses,
  normalizeNumber,
  uniqRandomNumber,
} from 'util/commons';
import ResponsiveHistoryTable from 'components/molecules/ResponsiveHistoryTable';
import {
  deleteEmissions,
  getEmissionWorkHistories,
} from 'apis/work_histories/emission';
import CustomPagination from 'components/atoms/CustomPagination';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import {
  EmissionBatchEditModal,
  EmissionEditModalEmissionData,
} from '../EmissionBatchEditModal';
import Portal from 'components/atoms/Portal';

export type Props = {
  id?: string;
  sites: Site[];
  isActive: boolean;
  filter?: GetEmissionParams;
  onPrintLabel?: (emission: WorkHistoriesEmissionsData) => void;
  wrapperStyle?: React.CSSProperties;
};

export const HistoryEmissions = ({
  sites,
  isActive = false,
  filter,
  onPrintLabel,
  wrapperStyle,
}: Props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [data, setData] = useState<WorkHistoriesEmissionsData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [args, setArgs] = useState<
    WorkHistoriesParams & {
      trigger?: number;
    }
  >({
    page: DEFAULT_PAGE,
    take: PAGE_SIZE,
  });
  const isAndroid = /Android/i.test(navigator.userAgent);
  const pageCount = useMemo(
    () => Math.ceil(totalRecords / PAGE_SIZE),
    [totalRecords]
  );
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingItems, setEditingItems] = useState<
    EmissionEditModalEmissionData[]
  >([]);

  useEffect(() => {
    if ((user.role !== RoleType.AdminOperator && !args.siteId) || !isActive) {
      return;
    }

    getEmissionWorkHistories({
      type: 'requests',
      params: args,
      successCallback: (data) => {
        setTotalRecords(data.total);
        setData(data.emissionsRequest);
      },
      failedCallback: () => {
        if (args.page === DEFAULT_PAGE) {
          setData([]);
          setTotalRecords(0);
        }
      },
    });
  }, [args, isActive, user.role]);

  useEffect(() => {
    setArgs((prevArgs) => ({
      ...prevArgs,
      ...filter,
      ...(user.role !== RoleType.AdminOperator && {
        siteId: sites[0]?.childSites[0]?.id,
      }),
      page: DEFAULT_PAGE,
      trigger: uniqRandomNumber(),
    }));
  }, [sites, filter, user.role]);

  useEffect(() => {
    setCheckedIds([]);
  }, [data]);

  const getStatus = useCallback(
    (emission: WorkHistoriesEmissionsData) => {
      switch (emission.emission.emissionStatus) {
        case EmissionStatus.Requested:
          return (
            <div className={`${styles.emissionType} ${styles.requested}`}>
              {t('history_emission_card.card_discharge_recovery_requested')}
            </div>
          );
        case EmissionStatus.UnRequest:
          return (
            <div className={`${styles.emissionType} ${styles.emission}`}>
              {t('history_emission_card.card_ejected')}
            </div>
          );
        default:
          return (
            <div className={`${styles.emissionType} ${styles.emission}`}>
              {t('history_emission_card.card_closed')}
            </div>
          );
      }
    },
    [t]
  );

  const handleSelectAll = () => {
    if (!data.length) {
      return;
    }

    if (checkedIds.length === data.length) {
      setCheckedIds([]);
    } else {
      setCheckedIds(data.map((item) => item.id));
    }
  };

  const handleCheckItem = (id: string) => {
    if (checkedIds.includes(id)) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
  };

  const isEditable = () => {
    return (
      user.role === RoleType.AdminSite || user.role === RoleType.UserNormal
    );
  };

  const siteNameViewable = () =>
    user.role === RoleType.AdminTenant || user.role === RoleType.AdminOperator;

  const columns: Column<WorkHistoriesEmissionsData>[] = useMemo(
    () => [
      ...(isEditable()
        ? [
            {
              Header: (
                <span
                  onClick={handleSelectAll}
                  style={{
                    cursor: data.length > 0 ? 'pointer' : 'not-allowed',
                    color: data.length > 0 ? 'inherit' : 'gray',
                  }}
                >
                  {t(
                    data.length === checkedIds.length && data.length > 0
                      ? 'common.deselect_all'
                      : 'common.select_all'
                  )}
                </span>
              ),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              accessor: 'checkbox' as any,
              className: styles.checkboxColumn,
              Cell: ({ row }) => {
                return (
                  <CustomCheckBox
                    id={row.original.id}
                    darkMode
                    checked={checkedIds.includes(row.original.id)}
                    onChange={() => handleCheckItem(row.original.id)}
                  />
                );
              },
            },
          ]
        : []),
      ...(siteNameViewable()
        ? [
            {
              Header: t('history_emission_card.card_site_name') || '',
              accessor: (row) => row.emission.site,
              Cell: ({ value }) => <>{value}</>,
            },
          ]
        : []),
      {
        Header: t('history_emission_card.card_registration_id') || '',
        accessor: 'id',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('history_emission_card.card_discharge_date_time') || '',
        accessor: (row) => row.emission.datetime,
        Cell: ({ value }) => (
          <>{getFormatDateWithCurrentTimezone(value, DATE_FORMAT.slaYMDHm)}</>
        ),
      },
      {
        Header: t('history_emission_card.card_content') || '',
        accessor: (row) => row.emission.item,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('history_emission_card.card_weight') || '',
        accessor: (row) => row.emission.weight,
        Cell: ({ value }) => <>{normalizeNumber({ value })}kg</>,
      },
      {
        Header: t('history_emission_card.card_status') || '',
        accessor: (row) => row.emission.emissionStatus,
        Cell: ({ row }) => getStatus(row.original),
      },
      ...(user.role !== RoleType.AdminTenant
        ? [
            {
              Header: t('history_emission_card.card_ejector') || '',
              accessor: (row: WorkHistoriesEmissionsData) =>
                row.emission.tenant,
              Cell: ({ value }: { value: string }) => <>{value}</>,
            },
          ]
        : []),
      ...(user.role !== RoleType.AdminTenant &&
      (user.role === RoleType.AdminOperator || user.temporaryStorageEnabled)
        ? [
            {
              Header: t('history_emission_card.card_temporary_storage') || '',
              accessor: (row) => row.emission.temporaryStorage?.name,
              Cell: ({ row }) => {
                if (!row.original.emission.temporaryStorage) {
                  return <>-</>;
                }

                const color =
                  TEMPORARY_STORAGE_COLORS_PALETTE[
                    Number(row.original.emission.temporaryStorage.color) %
                      TEMPORARY_STORAGE_COLORS_PALETTE.length
                  ];

                return (
                  <span
                    className={styles.temporaryStorageTag}
                    style={{
                      color:
                        getBrightnessFromColor(color) <= 100
                          ? '#ffffff'
                          : '#1b1b1b',
                      backgroundColor: color,
                    }}
                  >
                    {row.original.emission.temporaryStorage?.name}
                  </span>
                );
              },
            },
            {
              Header: t('emission_qr_scanning.storage_updated_at') || '',
              accessor: (row) => row.emission.storageUpdatedAt,
              Cell: ({ value }) => (
                <>
                  {value
                    ? getFormatDateWithCurrentTimezone(
                        value,
                        DATE_FORMAT.slaYMDHm
                      )
                    : '-'}
                </>
              ),
            },
          ]
        : []),
      {
        Header:
          t('history_emission_card.card_collection_request_date_time') || '',
        accessor: (row) => row.request?.datetime || '',
        Cell: ({ row }) => (
          <>
            {row.original.request && row.original.request.datetime !== ''
              ? getUtcFormatDateTime(
                  row.original.request.datetime,
                  DATE_FORMAT.slaYMDHm
                )
              : '-'}
          </>
        ),
      },
      {
        Header: t('history_emission_card.card_collection_requester') || '',
        accessor: (row) => row.request?.userId || '',
        Cell: ({ row }) => (
          <>
            {row.original.request && row.original.request.userId !== ''
              ? `${row.original.request.userId} (${row.original.request.userName})`
              : '-'}
          </>
        ),
      },
      ...(user.role !== RoleType.AdminTenant &&
      user.role !== RoleType.AdminOperator
        ? [
            {
              Header: t('history_emission_card.card_export') || '',
              accessor: (row: WorkHistoriesEmissionsData) => row.id,
              Cell: ({
                row,
              }: {
                row: { original: WorkHistoriesEmissionsData };
              }) => (
                <>
                  {onPrintLabel &&
                    (row.original.emission.emissionStatus ===
                      EmissionStatus.Requested ||
                    row.original.emission.emissionStatus ===
                      EmissionStatus.UnRequest ? (
                      <div className={styles.printLabelButton}>
                        {user.printLabel === PrintLabel.QRCode ? (
                          <button onClick={() => onPrintLabel(row.original)}>
                            {t(
                              'history_emission_card.card_print_qr_label_button'
                            )}
                          </button>
                        ) : isAndroid ? (
                          <button onClick={() => onPrintLabel(row.original)}>
                            {t('history_emission_card.card_print_label_button')}
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <div className={styles.printLabelButton}>
                        {isAndroid ? (
                          <button onClick={() => onPrintLabel(row.original)}>
                            {t('history_emission_card.card_print_label_button')}
                          </button>
                        ) : null}
                      </div>
                    ))}
                </>
              ),
            },
          ]
        : []),
      {
        Header: () => (
          <div>
            <div>{t('history_emission_card.card_memo') || 'メモ'}</div>
            <div className={styles.memoHint}>
              {t('history_emission_card.card_memo_hint')}
            </div>
          </div>
        ),
        id: 'memo',
        accessor: (row) => row.emission.memo,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
    ],
    [
      t,
      user.role,
      user.printLabel,
      onPrintLabel,
      getStatus,
      data,
      checkedIds,
      data.length,
    ]
  );

  const handleEditItems = () => {
    if (!checkedIds.length) {
      return;
    }

    setEditingItems(
      data
        .filter((item) => checkedIds.includes(item.id))
        .map(({ emission, id, collect }) => ({
          ...emission,
          id,
          collectUserName: collect.operatorName,
        }))
    );
  };

  const handleDeleteItems = () => {
    if (!checkedIds.length) {
      return;
    }

    if (!showDeleteModal) {
      setShowDeleteModal(true);

      return;
    }

    deleteEmissions({
      emissionIds: checkedIds,
      successCallback: () => {
        setShowDeleteModal(false);
        setCheckedIds([]);
        setArgs((prev) => ({
          ...prev,
          trigger: uniqRandomNumber(),
          page:
            args.page === pageCount &&
            pageCount > 1 &&
            checkedIds.length === data.length
              ? pageCount - 1
              : prev.page,
        }));
      },
    });
  };

  return (
    <div className={styles.historyEmissions} style={wrapperStyle}>
      <ResponsiveHistoryTable<WorkHistoriesEmissionsData>
        data={data}
        columns={columns}
        noDataMessage={t('messages.M_008') ?? ''}
        scrollResetDependencies={[filter, data]}
        className={styles.historyTable}
      />

      <div className={styles.paginationContainer}>
        {data.length > 0 && isEditable() && (
          <div className={styles.actionButtons}>
            <button
              className={mergeClasses(styles.editButton, {
                [styles.disabledButton]: !checkedIds.length,
              })}
              onClick={handleEditItems}
            >
              {t('common.button.edit_data')}
            </button>
            <button
              className={mergeClasses(styles.deleteButton, {
                [styles.disabledButton]: !checkedIds.length,
              })}
              onClick={handleDeleteItems}
            >
              {t('common.button.delete')}
            </button>
          </div>
        )}

        <CustomPagination
          pageCount={pageCount}
          onPageChange={({ selected }) =>
            setArgs((prev) => ({ ...prev, page: selected + 1 }))
          }
          forcePage={args.page - 1}
        />
      </div>

      {showDeleteModal && (
        <Portal>
          <DeleteItemDarkModal
            onClose={() => {
              setShowDeleteModal(false);
            }}
            onHandleDelete={handleDeleteItems}
            content={t('common.confirm_delete_no_field')}
          />
        </Portal>
      )}

      {editingItems.length > 0 && (
        <EmissionBatchEditModal
          emissionData={editingItems}
          onClose={() => setEditingItems([])}
          afterSubmit={(type) => {
            if (type === 'changed') {
              setArgs((prev) => ({ ...prev, trigger: uniqRandomNumber() }));
            } else {
              setCheckedIds([]);
            }
          }}
        />
      )}
    </div>
  );
};
